<template>
  <div class="container">
    <div>
      <div v-for="(item, index) in radiologyData" :key="item">
        <div class="radiologyItem" @click="handleClick(index, item)" :class="{ 'selected': selectedIndex === index }">
          <div class="itemBox">
            <div class="data">
              <div class="label">Imaging Date</div>
              <span>
                {{ formatDate(item.date) }}
              </span>
            </div>
            <div class="data">
              <div class="label">Imaging Type</div>
              <span class="bold">{{ item.imagingType || 'None' }}</span>
            </div>
            <div class="data">
              <div class="label">Test Type</div>
              <span>
                {{ item.testType || 'N/A' }}
              </span>
            </div>
            <div class="location">
              <span class="material-symbols-outlined size-24 location-icon">
                location_on
              </span>
              <span class="location-text">{{ item.labName || '—' }}</span>
            </div>
            <div v-if="item.mimeType" class="report-only">
              <span>{{ item.mimeType.includes('pdf') ? 'PDF' : 'TXT' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, defineEmits, defineExpose, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { getRadiology, getRadiologyViewerUrl } from './getRadiology'
import { getPatientId } from './getPatientId'
import { formatDate } from '@/utils/utils'
import { getReportFile } from '@/ux3/cards/ReportViewer/getReportFile.js'
import { accessionNumberRegex } from '@/settings'

const store = useStore()

const emit = defineEmits(['onRadiologyCountChange'])

const patientMrn = computed(() => store.getters.session?.patient?.id || '')

const radiologyData = ref([])
const selectedIndex = ref(-1)

const handleClick = async (index, item) => {
  selectedIndex.value = index
  // if clicked item is a report
  if (item.id && item.mimeType) {
    store.dispatch('showViewer', true)
    store.dispatch('reportDocType', item.mimeType)
    store.dispatch('reportDocument', await getReportFile(item.id, item.mimeType))
  } else {
    if (!accessionNumberRegex.test(item.accessionNumber)) { return }

    const radiologyViewerUrl = await getRadiologyViewerUrl(item.accessionNumber)
    if (radiologyViewerUrl) {
      window.open(radiologyViewerUrl, '_blank', `newwindow`)
    }
  }
}

const loadSamples = async (patientMrn) => {
  if (!patientMrn) { return }
  const patientId = await getPatientId(patientMrn)
  const results = await getRadiology(patientId, patientMrn)
  radiologyData.value = results || []

  emit('onRadiologyCountChange', radiologyData.value.length)
}

watch(patientMrn, async () => {
  await loadSamples(patientMrn.value)
})

onMounted(async () => {
  await loadSamples(patientMrn.value)
})

defineExpose({ loadSamples })
</script>

<style lang="scss" scoped>
.bold {
  font-weight: bold;
}

.data {
  color: var(--Black);
  margin-bottom: 15px;
}

.itemBox {
  padding: 1rem;
}

.radiologyItem {
  display: flex;
  justify-content: space-between;
  text-align: left;
  border-radius: 10px;
  transition: all linear 200ms;
  background-color: var(--White);
  overflow: auto;
  position: relative;
  text-align: left;
  margin: 0.75rem;
  padding: 0.75rem;
  font-size: 0.8rem;

  &:hover {
    cursor: pointer;
    box-shadow: 5px 5px 5px var(--Grey500);
    transition: all ease-out 200ms;
  }

  &.selected {
    box-shadow: 3px 3px 10px var(--Grey800);
    transition: all ease-out 200ms;
  }

  .report-only {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--Grey700);
    color: var(--White);
    position: absolute;
    border-radius: 5px;
    font-size: 70%;
    top: 1rem;
    right: 2rem;
    height: 20px;
    width: 35px;
  }
}

.location {
  color: var(--Grey500);
  display: flex;
  flex-direction: row;
  font-size: 90%;
}

.location-icon {
  padding-bottom: 3px;
}

.location-text {
  padding-top: 3px;
}
</style>
